import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useConsent, type ConsentTier } from "@/contexts/consent-context";
import { useAuth } from "@/hooks/use-auth";
import type { TrackingScript } from "@shared/schema";

/**
 * Fetches active tracking scripts from the backend and injects them into
 * <head> based on the user's current consent preferences.
 *
 * Scope:
 * - Only runs for unauthenticated users on public pages (not /admin or /dashboard).
 *   Authenticated users are excluded from the consent flow entirely.
 *
 * Consent revocation + server-side deactivation:
 * - When a user revokes a tier, injected script nodes are removed from <head>.
 * - When the server response changes (e.g. a script is deactivated/deleted),
 *   previously injected nodes for that script ID are also removed (reconciliation).
 * - DOM cleanup is best-effort; a page reload provides complete isolation.
 */
export function TrackingScriptInjector() {
  const [location] = useLocation();
  const isPublic = !location.startsWith("/admin") && !location.startsWith("/dashboard");
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { hasConsent } = useConsent();

  const shouldRun = isPublic && !authLoading && !isAuthenticated;

  // Maps scriptId → injected <script> elements so we can remove them on revocation/deactivation
  const injectedNodes = useRef<Map<string, HTMLScriptElement[]>>(new Map());

  const { data: scripts = [] } = useQuery<TrackingScript[]>({
    queryKey: ["/api/public/tracking-scripts"],
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled: shouldRun,
  });

  const syncScripts = (scriptList: TrackingScript[]) => {
    const currentIds = new Set(scriptList.map(s => s.id));

    // Reconciliation: remove any injected scripts no longer in server response
    for (const [id, nodes] of injectedNodes.current.entries()) {
      if (!currentIds.has(id)) {
        nodes.forEach(el => {
          try { el.parentNode?.removeChild(el); } catch { /* already removed */ }
        });
        injectedNodes.current.delete(id);
      }
    }

    for (const script of scriptList) {
      const tier = script.consentTier as ConsentTier;
      const consented = hasConsent(tier);
      const alreadyInjected = injectedNodes.current.has(script.id);

      if (consented && !alreadyInjected) {
        // Inject
        try {
          const template = document.createElement("div");
          template.innerHTML = script.scriptContent;
          const tags = template.querySelectorAll("script");
          const nodes: HTMLScriptElement[] = [];

          if (tags.length > 0) {
            tags.forEach(original => {
              const el = document.createElement("script");
              Array.from(original.attributes).forEach(attr => el.setAttribute(attr.name, attr.value));
              el.textContent = original.textContent;
              document.head.appendChild(el);
              nodes.push(el);
            });
          } else {
            // Raw JS
            const el = document.createElement("script");
            el.textContent = script.scriptContent;
            document.head.appendChild(el);
            nodes.push(el);
          }

          injectedNodes.current.set(script.id, nodes);
        } catch (err) {
          console.warn(`[TrackingScriptInjector] Failed to inject "${script.name}":`, err);
        }
      } else if (!consented && alreadyInjected) {
        // Revoke — remove injected nodes from DOM
        const nodes = injectedNodes.current.get(script.id) ?? [];
        nodes.forEach(el => {
          try { el.parentNode?.removeChild(el); } catch { /* already removed */ }
        });
        injectedNodes.current.delete(script.id);
      }
    }
  };

  useEffect(() => {
    if (shouldRun && scripts.length > 0) syncScripts(scripts);
  }, [scripts, shouldRun]);

  useEffect(() => {
    const onConsentChanged = () => {
      if (shouldRun && scripts.length > 0) syncScripts(scripts);
    };
    window.addEventListener("consentChanged", onConsentChanged);
    return () => window.removeEventListener("consentChanged", onConsentChanged);
  }, [scripts, hasConsent, shouldRun]);

  return null;
}
